<template>
  <form class="contents" @submit.prevent="save">
    <tabs />
    <toolbar />
    <menu-bar class="flex justify-center">
      <div class="w-card-content flex justify-end gap-x-7.5">
        <custom-button variant="primary" type="submit" :busy="saving">
          {{ $t('save') }}
        </custom-button>
      </div>
    </menu-bar>
    <busy-area :busy="loadingSettings">
      <cards class="mb-20">
        <card
          v-for="(agbItem, index) in internalAgb"
          :key="agbItem.id"
          :title="$t('pages.agb.' + agbItem.name + '.title')"
        >
          <section class="card-section">
            <div v-if="agbItem.versioned">
              <div class="flex gap-x-5 pb-2">
                <FormInput
                  v-model="agbItem.versionNumber"
                  type="number"
                  :error="errors?.[`[${index}]`]?.versionNumber"
                  :label="$t('pages.agb.label.versionNumber')"
                  class="w-full"
                />
                <DatePicker
                  v-model="agbItem.versionDate"
                  mode="datetime"
                  :error="errors?.[`[${index}]`]?.versionDate"
                  :label="$t('pages.agb.label.versionDate')"
                  class="w-full"
                />

              </div>
              <p class="text-sm text-table-text-gray">
                {{ $t('pages.agb.label.helpText') }}
              </p>
            </div>
            <translation-accordion v-slot="{ translation }" v-model="internalAgb[index]">
              <HtmlEditor
                v-model="translation.text"
                name="text"
                :error="errors?.[`[${index}]`]?.text"
                :label="$t('text')"
              />
            </translation-accordion>
          </section>
        </card>
      </cards>
    </busy-area>
  </form>
</template>

<script lang="ts" setup>
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Cards from '@components/utils/Cards.vue';
import Card from '@components/utils/Card.vue';
import { useI18n } from 'vue-i18n';
import CustomButton from '@utils/CustomButton.vue';
import {
  array, date, number, object, string,
} from 'yup';
import { computed } from 'vue';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import BusyArea from '@/components/utils/busy/BusyArea.vue';
import MenuBar from '@/components/utils/MenuBar.vue';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import { useFetchAction, useFetchQuery } from '@/hooks/fetch';
import { useNotify } from '@/components/utils/notifications';
import { useValidation } from '@/hooks/form';
import type { Agb, HydraCollection, HydraMember } from '@/entities/Entities';
import { syncRef } from '@/hooks/state';
import FormInput from '@/components/utils/inputs/Input.vue';
import DatePicker from '@/components/utils/inputs/DatePicker.vue';

const { t } = useI18n();

const {
  data: agbCollection,
  loading: loadingSettings,
  refetch,
} = useFetchQuery<HydraCollection<Agb>>('/agbs');

const originalAgb = computed(() => agbCollection.value?.['hydra:member'] ?? []);

const [exec, { loading: saving }] = useFetchAction();

const getAgbCopy = (currentAgb: Agb[]) =>
  currentAgb.map((agb) => ({
    ...agb,
    translations: [...agb.translations],
    versionDate: new Date(agb.versionDate),
  }));

const internalAgb = syncRef(originalAgb, getAgbCopy);

const notify = useNotify();

const replaceIdWithAtId = <T extends Partial<HydraMember> | undefined>(obj: T): T => ({
  ...obj,
  id: obj?.['@id'] ?? null,
});

const replaceIdWithAtIdInArray = <S extends Partial<HydraMember> | undefined>(
  arr: S[] | undefined,
): S[] | undefined => arr?.map((obj) => replaceIdWithAtId(obj));

const {
  data: agb,
  errors,
  validatedFn,
} = useValidation(
  array().of(
    object({
      versionNumber: number()
        .integer(t('validation.number'))
        .typeError(t('validation.number'))
        .min(0, t('validation.nonNegative'))
        .required(t('validation.notEmpty')),
      versionDate: date().required(),
      translations: array().of(
        object({
          id: string(),
          text: string().required(),
          language: string().required(),
        }),
      ),
    }),
  ),
  internalAgb,
);

const save = validatedFn(async () => {
  let error = false;
  await agb.value.reduce(async (promise, agbItem) => {
    await promise;
    try {
      if (!error) {
        await exec(`/agbs/${agbItem.id}`, {
          method: 'PUT',
          body: {
            translations: replaceIdWithAtIdInArray(agbItem.translations),
            versionNumber: agbItem.versioned ? agbItem.versionNumber : undefined,
            versionDate: agbItem.versioned ? agbItem.versionDate : undefined,
          },
        });
      }
    } catch (e) {
      error = true;
    }
  }, Promise.resolve());

  if (!error) {
    notify?.({
      variant: 'success',
      title: t('pages.agb.saveSuccess'),
    });
    await refetch();
  } else {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
});
</script>

<style lang="scss" scoped>
form {
  --card-padding: #{px(60)};
}
.card-section {
  @apply grid grid-cols-1 gap-x-5 gap-y-7.5 w-full;
}
.form-group {
  @apply flex flex-col gap-y-7.5;
  @apply px-7.5 py-4;
  @apply rounded-4 border border-table-head-gray;
  @apply -mx-7.5;
}

.trash {
  @apply bg-red-100;
  @apply h-10 w-10;
  @apply p-2.5;
  @apply inline-block;
  @apply ml-2;
  border-radius: 100%;
}
</style>
